import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { createMarkdownComponents } from '../components/common/MarkdownComponents';

import MarkdownRenderer from '../components/documentation/MarkdownRenderer';

const API_URL = process.env.REACT_APP_API_URL || '';

function queryExtension({ query, post_ids = null, onSources, onToken, onDone, onError }) {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({ query });

  if (token) params.append('token', token);
  if (post_ids && post_ids.length) params.append('post_ids', post_ids.join(','));

  const url = `${API_URL}/adss/v1/extension/query?${params}`;
  const es = new EventSource(url);

  es.onopen = () => {
    console.log('[queryExtension] EventSource opened');
  };

  es.addEventListener('sources', (e) => {
    try {
      const parsed = JSON.parse(e.data);
      onSources?.(parsed.sources || []);
    } catch (err) {
      console.warn('[queryExtension] failed to parse sources:', err, e.data);
    }
  });

  es.onmessage = (e) => {
    try {
      const parsed = JSON.parse(e.data);

      if (parsed.done) {
        onDone?.();
        es.close();
        return;
      }

      if (parsed.error) {
        onError?.(parsed.error);
        es.close();
        return;
      }

      if (typeof parsed.token === 'string') {
        onToken?.(parsed.token);
      }
    } catch (err) {
      console.warn('[queryExtension] failed to parse token payload:', err, e.data);
    }
  };

  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) return;
    onError?.('Connection lost');
    es.close();
  };

  return () => {
    console.log('[queryExtension] closing EventSource');
    es.close();
  };
}

const AiBridgeWidget = () => {
  const [input, setInput] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const answerBufferRef = useRef('');
  const flushTimerRef = useRef(null);

  const scheduleFlush = () => {
    if (flushTimerRef.current) return;

    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      setAnswer(answerBufferRef.current);
    }, 50);
  };

  useEffect(() => {
    return () => {
      abortRef.current?.();
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    abortRef.current?.();

    answerBufferRef.current = '';
    setAnswer('');
    setSources([]);
    setError(null);
    setLoading(true);

    abortRef.current = queryExtension({
      query: input.trim(),
      onSources: (s) => {
        setSources(s);
      },
      onToken: (t) => {
        answerBufferRef.current += t;
        scheduleFlush();
      },
      onDone: () => {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        setAnswer(answerBufferRef.current);
        setLoading(false);
      },
      onError: (msg) => {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        setError(msg);
        setLoading(false);
      },
    });
  };

  const handleStop = () => {
    abortRef.current?.();
    abortRef.current = null;

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    setAnswer(answerBufferRef.current);
    setLoading(false);
  };

  const normalizeMarkdown = (text) => {
    if (!text) return '';

    return text
      // ensure fenced blocks start on their own line
      .replace(/([^\n])```/g, '$1\n```')
      // ensure something after ``` starts on a new line if needed
      .replace(/```([^\n])/g, '```\n$1')
      // optional: avoid too many blank lines
      .replace(/\n{3,}/g, '\n\n');
  };

  return (
    <div className="py-12 bg-ui-light-background dark:bg-ui-dark-background">
      <div className="px-4 mx-auto max-w-3xl sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Ask a question about the documentation
          </h2>
          <p className="mt-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
            Powered by the documentation knowledge base
          </p>
          <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            Experimental feature. Responses may be incomplete or incorrect — verify important information in the documentation and use with care.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask something about the documentation..."
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary placeholder-ui-light-text-secondary dark:placeholder-ui-dark-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
          {loading ? (
            <button
              type="button"
              onClick={handleStop}
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
            >
              Stop
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium transition-colors disabled:opacity-40"
            >
              Ask
            </button>
          )}
        </form>

        {error && (
          <p className="mt-3 text-sm text-red-500 dark:text-red-400">{error}</p>
        )}

        {sources.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {sources.map((s, i) => {
              const tag = s.route ?? `post ${s.post_id}`;
              const href = `/documentation/${tag}`;

              return (
                <Link
                  key={i}
                  to={href}
                  className="px-2 py-1 text-xs rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/60 transition-colors"
                >
                  {tag} · {(s.score * 100).toFixed(0)}%
                </Link>
              );
            })}
          </div>
        )}

        {(answer || loading) && (
          <div className="mt-4 p-4 rounded-lg border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card">
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:leading-relaxed prose-pre:overflow-x-auto prose-pre:rounded-md prose-pre:p-4 prose-code:before:content-none prose-code:after:content-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={createMarkdownComponents(document.documentElement.classList.contains('dark'))}
              >
                {answer}
              </ReactMarkdown>


              {loading && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-primary-500 animate-pulse align-text-bottom" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiBridgeWidget;